import { debounce, isClickedOutside, onIdleAfterDOMReady, onIdleAfterPageLoad, onAnimationEnd } from '@theme/utilities';
import { getFocusableElements } from '@theme/focus';
import { DialogComponent } from "@theme/dialog";
import { DialogOpenEvent, DialogCloseEvent, DropdownDialogOpenEvent, DropdownDialogCloseEvent, MenuDropdownOpenEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { bindCloseCursor } from '@theme/custom-cursor';
import { Component } from "@theme/component";
import { RecentlyViewed, RecentlySearched } from '@theme/storage';

class SearchDrawerComponent extends DialogComponent {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(DialogOpenEvent.eventName, this.#handleOpenDialog);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleCloseDialog);
  }

  /**
   * Opens search drawer and adjusts direction based on trigger element
   * @param {Event} e - Open event
   */
  openDialog(e) {
    this.#changeDrawerDirection(e.target);
    super.openDialog(e);
  }

  /**
   * Handles Shopify theme editor design mode
   * Opens drawer when section is selected, closes when deselected
   */
  handleDesignMode() {
    const section = this.closest('.shopify-section');
    this.handleSectionSelect = (event) => {
      const searchBar = document.querySelector('search-bar-component');
      if (searchBar) {
        this._editorSearchBar = searchBar;
        searchBar.openPopup();
      } else {
        this.openDialog(event);
      }
    };
    section.addEventListener('shopify:section:select', this.handleSectionSelect);

    this.handleSectionDeselect = (event) => {
      if (this._editorSearchBar) {
        this._editorSearchBar.closePopup();
        this._editorSearchBar = null;
      } else {
        this.closeDialog();
      }
    };
    section.addEventListener('shopify:section:deselect', this.handleSectionDeselect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleOpenDialog);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleCloseDialog);
    const section = this.closest('.shopify-section');
    if (section) {
      if (this.handleSectionSelect) {
        section.removeEventListener('shopify:section:select', this.handleSectionSelect);
      }
      if (this.handleSectionDeselect) {
        section.removeEventListener('shopify:section:deselect', this.handleSectionDeselect);
      }
    }
  }

  /**
   * Focuses search input when drawer opens
   */
  #handleOpenDialog = (event) => {
    const { predictiveSearch } = this.refs;
    predictiveSearch.refs.predictiveSearchInput.focus();
  }

  /**
   * Resets search state when drawer closes
   */
  #handleCloseDialog = (event) => {
    const { predictiveSearch } = this.refs;
    predictiveSearch.resetSearch(false);
  }

  /**
   * Changes drawer direction based on which header column triggered it
   * Updates both desktop and mobile drawer classes
   * @param {HTMLElement} target - Element that triggered the drawer open
   */
  #changeDrawerDirection(target) {
    if (!target) return;
    if(target.classList.contains('shopify-section')) return;

    const dialog = this.refs.dialog;
    if (!dialog) return;

    // Determine position based on header column (left or right)
    const position = target.closest('.header__column--left') ? 'left' : 'right';

    // Update desktop drawer class
    const existingDesktopDrawer = Array.from(dialog.classList).find(className =>
      className.startsWith('dialog--drawer-')
    );
    if (existingDesktopDrawer) {
      dialog.classList.remove(existingDesktopDrawer);
      dialog.classList.add(`dialog--drawer-${position}`);
    }

    // Update mobile drawer class
    const existingMobileDrawer = Array.from(dialog.classList).find(className =>
      className.startsWith('dialog--mobile-drawer-')
    );
    if (existingMobileDrawer) {
      dialog.classList.remove(existingMobileDrawer);
      dialog.classList.add(`dialog--mobile-drawer-${position}`);
    }
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }

}
if (!customElements.get('search-drawer-component')) {
  customElements.define('search-drawer-component', SearchDrawerComponent);
}

class SearchPredictiveComponent extends Component {
  #activeFetch = null;
  #sharedSectionId = null;
  #cursorBinding = null;

  connectedCallback() {
    super.connectedCallback();
    this.searchLimit = 6;
    this.addEventListener(DialogCloseEvent.eventName, this.#resetSearch);
    this.addEventListener('submit', this.#handleSubmitted);

    if (this.context === 'page') {
      this.refs.predictiveSearchInput.addEventListener('focus', this.#onFocus);
      document.addEventListener('click', this.#handleClickOutside);
      this.addEventListener('keydown', this.#handleKeydown);

      if (this.searchOverlay) {
        this.searchOverlay.addEventListener('click', this.#handleSearchOverlayClick);
        this.#setupCursorListeners();
      }
    }

    // Update height for page context
    if (this.context === 'page') {
      this.#updatePredictiveSearchHeight();
      this.debouncedUpdatePredictiveSearchHeight = debounce(this.#updatePredictiveSearchHeight.bind(this), 100);
      window.addEventListener('resize', this.debouncedUpdatePredictiveSearchHeight);

      onIdleAfterDOMReady(() => {
        this.#checkInputAndSearch();
      });
    }

    if (this.showRecentSearches) {
      if (RecentlySearched.getKeyWords().length > 0) {
        onIdleAfterPageLoad(() => this.#renderRecentlySearched());
      } else {
        this.refs.recentlySearched?.remove();
      }
    }

    // Sync empty-state fetches to drawer's sectionId so all instances share the same fetch URL
    if (this.context !== 'drawer') {
      const drawerSectionId = document.querySelector('search-drawer-component')?.dataset.sectionId;
      if (drawerSectionId) {
        this.#sharedSectionId = drawerSectionId;
        this.querySelectorAll('recently-viewed').forEach(el => {
          el.dataset.sectionId = drawerSectionId;
        });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(DialogCloseEvent.eventName, this.#resetSearch);
    this.removeEventListener('submit', this.#handleSubmitted);

    if (this.context === 'page') {
      this.refs.predictiveSearchInput.removeEventListener('focus', this.#onFocus);
      document.removeEventListener('click', this.#handleClickOutside);
      this.removeEventListener('keydown', this.#handleKeydown);

      if (this.searchOverlay) {
        this.searchOverlay.removeEventListener('click', this.#handleSearchOverlayClick);
        this.#removeCursorListeners();
      }

      window.removeEventListener('resize', this.debouncedUpdatePredictiveSearchHeight);
    }


    // Abort any pending fetch
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  /**
   * Saves search query to recently searched when form is submitted
   */
  #handleSubmitted = (e) => {
    RecentlySearched.addKeyWords(this.#getQuery());
  }

  /**
   * Gets trimmed search query from input
   * @returns {string} Search query
   */
  #getQuery() {
    return this.refs.predictiveSearchInput.value.trim();
  }

  /**
   * Builds predictive search URL with query and resource limits
   * @returns {URL} Configured search URL
   */
  #buildUrl() {
    let searchTerm = this.#getQuery();
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('drawer', 'search');
    url.searchParams.set('resources[limit]', this.searchLimit);
    url.searchParams.set('resources[limit_scope]', 'each');
    return url;
  }

  /**
   * Resets search state with optional focus retention
   * Debounced to prevent rapid calls
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus && this.context !== 'page') {
      this.refs.predictiveSearchInput.focus();
    }
    this.#resetSearch();

    if (this.context === 'page') {
      this.classList.remove('search-open');
      this.#deactivateCursor();
      if (this.classList.contains('has-keywords')) {
        this.querySelector('form')?.requestSubmit();
      } else {
        this.refs.predictiveSearchInput.focus();
      }
    }

    this.predictiveSearchReset?.setAttribute('tabindex', '-1');
    this.predictiveSearchSubmit?.setAttribute('tabindex', '0');
  }, 100);

  /**
   * Handles search input changes
   * Debounced to avoid excessive API calls
   * Only triggers on actual input (not programmatic changes)
   * @param {Event} event - Input event
   */
  search = debounce((event) => {
    if (!event.inputType) return;

    if (!this.#getQuery().length) {
      this.#resetSearch();
      return;
    }

    const url = this.#buildUrl();
    this.#fetchSearchResults(url);
    this.predictiveSearchReset?.setAttribute('tabindex', '0');
    this.predictiveSearchSubmit?.setAttribute('tabindex', '-1');
  }, 300);

  /**
   * Fetches search results via Section Rendering API
   * Aborts previous requests to prevent race conditions
   * Updates UI with results or shows empty state
   * @param {URL} url - Search URL with query parameters
   * @param {boolean} showLoadingState - Whether to show loading state
   */
  async #fetchSearchResults(url, showLoadingState = true) {
    if (!this.dataset.sectionId) return;

    const { predictiveSearchResults, predictiveSearchEmpty, predictiveSearchActions, predictiveSearchFooter } = this.refs;

    this.#activeFetch?.abort();
    const fetchController = new AbortController();
    this.#activeFetch = fetchController;

    if (showLoadingState) {
    this.classList.add('searching');
    predictiveSearchActions?.classList.add('loading-state');
    }

    const useCache = Shopify.designMode ? false : true;

    sectionRenderer
      .getSectionHTML(this.sectionId, useCache, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;
        // Skip if a newer request has superseded this one
        if (fetchController.signal.aborted) return;

        const parseHtml = new DOMParser().parseFromString(resultsMarkup, 'text/html');
        const results = parseHtml.querySelector('[ref="predictiveSearchResults"]');
        if (!results) return;

        morph(predictiveSearchResults, results);

        predictiveSearchEmpty.hidden = true;
        predictiveSearchResults.hidden = false;
        predictiveSearchFooter.hidden = false;
        this.refs.predictiveSearchInput?.setAttribute('aria-expanded', 'true');

        // Announce result count via the persistent live region (the in-result
        // status sits inside a toggled-hidden container, so it isn't reliable).
        const status = predictiveSearchResults.querySelector('[data-results-status]')?.textContent?.trim();
        if (status && this.refs.searchLiveRegion) this.refs.searchLiveRegion.textContent = status;
      })
      .catch((error) => {
        if (fetchController.signal.aborted) return;
        console.error('Predictive search failed', error);
      }).finally(() => {
        if (showLoadingState) {
        predictiveSearchActions?.classList.remove('loading-state');
          this.classList.remove('searching');
        }
      });
  }

  /**
   * Resets search UI to initial state
   * Clears input, shows empty state, hides results
   */
  #resetSearch = async () => {
    const { predictiveSearchInput, predictiveSearchEmpty, predictiveSearchResults, predictiveSearchFooter } = this.refs;
    this.classList.remove('searching');
    predictiveSearchInput.value = '';
    predictiveSearchEmpty.hidden = false;
    predictiveSearchResults.hidden = true;
    predictiveSearchFooter.hidden = true;
    predictiveSearchInput.setAttribute('aria-expanded', 'false');
  }

  /**
   * Renders recently searched keywords from localStorage
   * Fetches search results for saved keywords and morphs into DOM
   */
  #renderRecentlySearched = async () => {
    if (!this.isConnected) return;

    const { recentlySearched } = this.refs;
    if (!recentlySearched) return;

    const keyWords = RecentlySearched.getKeyWords();
    if (keyWords.length === 0) {
      recentlySearched.remove();
      return;
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', keyWords.map((id) => `key:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'query');

    await sectionRenderer.fetchAndMorphContent({
      sectionId: this.#sharedSectionId ?? this.sectionId,
      url: url,
      targetElement: recentlySearched,
      sourceSelector: '[ref="recentlySearched"]',
      cache: true,
      componentElement: this,
      removeHiddenAttribute: true,
      checkLoadingStates: true
    });
  }

  /**
   * Views all results by submitting the search form
   */
  viewAllResults = () => {
    const searchForm = this.querySelector('form');
    if (searchForm) {
      searchForm.requestSubmit();
    }
  }

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewed } = this.refs;
    if (!recentlyViewed) return;
    recentlyViewed.querySelector('.predictive-search__list')?.remove();
    recentlyViewed.hidden = true;
  }

  /**
   * Clears the recently searched.
   * @param {Event} event - The event.
   */
  clearRecentlySearched(event) {
    event.stopPropagation();

    RecentlySearched.clearKeyWords();

    const { recentlySearched } = this.refs;
    if (!recentlySearched) return;
    recentlySearched.remove();
  }

  /**
   * Shows search results panel when input is focused (page context only)
   */
  #onFocus = () => {
    this.classList.add('search-open');
  }

  /**
   * Hides search results panel when clicking outside (page context only)
   * @param {Event} event - Click event
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.classList.remove('search-open');
      this.#deactivateCursor();
    }
  };

  /**
   * Hides search results panel when clicking on the search overlay
   * @param {Event} event - Click event
   */
  #handleSearchOverlayClick = (event) => {
    this.classList.remove('search-open');
    this.#deactivateCursor();
  };

  /**
   * Hides search results panel when pressing Escape (page context only)
   * @param {KeyboardEvent} event - Keydown event
   */
  #handleKeydown = (event) => {
    if (event.key === 'Escape') {
      this.classList.remove('search-open');
      this.#deactivateCursor();
      this.predictiveSearchReset?.setAttribute('tabindex', '-1');
      this.predictiveSearchSubmit?.setAttribute('tabindex', '0');
    }
  };

  /**
   * Checks if input has data and triggers search if needed
   */
  #checkInputAndSearch = () => {
    const inputValue = this.refs.predictiveSearchInput.value.trim();
    const inputHasData = inputValue.length > 0;

    if (inputHasData && this.#getQuery().length > 0) {
      const url = this.#buildUrl();
      this.#fetchSearchResults(url, false);
    }
  }

  /**
   * Updates the height CSS variable for predictive search in page context
   * Calculates available height from component offset to document bottom
   * Limits to maximum 900px
   */
  #updatePredictiveSearchHeight = () => {
    if (!this.classList.contains('predictive-search--page')) return;

    const offsetTop = this.getBoundingClientRect().top + window.scrollY;
    const componentHeight = this.offsetHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const availableHeight = documentHeight - offsetTop - componentHeight - 50;
    const height = Math.min(availableHeight, 700);

    this.style.setProperty('--predictive-search-height', `${height}px`);
  }

  get context() {
    return this.dataset.context;
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }

  get showProductRecommendation() {
    return this.dataset.showProductRecommendation === 'true';
  }

  get showRecentSearches() {
    return this.dataset.showRecentSearches === 'true';
  }

  get searchEmptyId() {
    return 'predictive-search-empty';
  }

  get searchOverlay() {
    return this.refs.predictiveSearchOverlay;
  }

  /**
   * Wires the follow-cursor to the page overlay so it shows a close cursor
   * while hovering the dimmed area, mirroring the search popup and dialogs.
   */
  #setupCursorListeners() {
    const overlay = this.searchOverlay;
    if (!overlay) return;

    this.#cursorBinding?.stop();
    this.#cursorBinding = bindCloseCursor({
      root: overlay,
      // The results panel is absolutely positioned, so it falls outside this component's own box
      except: () => [this.refs.searchForm, this.refs.predictiveSearchContent],
      mask: [overlay, [document.documentElement, 'search-cursor-active']],
    });
  }

  #removeCursorListeners() {
    this.#cursorBinding?.stop();
    this.#cursorBinding = null;
  }

  #deactivateCursor = () => {
    this.#cursorBinding?.leave();
  };

  get predictiveSearchReset() {
    return this.querySelector('.predictive-search__reset');
  }

  get predictiveSearchSubmit() {
    return this.querySelector('.predictive-search__submit');
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', SearchPredictiveComponent);
}

class SearchBarComponent extends Component {
  static VIEWPORT_PADDING = 8;

  #isOpen = false;
  #container = null;
  #overlay = null;
  #predictiveSearch = null;
  #resizeHandler = null;
  #cursorBinding = null;
  #searchingObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#container = this.refs.searchBarPopup;
    this.#overlay = this.refs.searchBarOverlay;
    this.#predictiveSearch = this.refs.predictiveSearch;

    // Borrow section ID from search-drawer so results fetch from the right section
    const drawerSectionId = document.querySelector(
      'predictive-search-component[data-context="drawer"]'
    )?.dataset.sectionId;
    if (drawerSectionId && this.#predictiveSearch) {
      this.#predictiveSearch.dataset.sectionId = drawerSectionId;
    }

    if (this.#container) {
      document.body.appendChild(this.#container);
      // Tab navigation through the results panel (real focus, panel lives in <body>).
      this.#container.addEventListener('keydown', this.#handleResultsKeydown, { signal: this.abortController.signal });
    }
    if (this.#overlay) {
      this.#overlay.setAttribute('hidden', '');
      document.body.appendChild(this.#overlay);
    }

    const topRow = this.closest('.header__row--top');
    if (topRow) {
      const headerComponent = this.closest('header-component');
      headerComponent?.classList.add('has-search-bar-top');
      const setTopHeight = () => {
        const h = topRow.offsetHeight;
        if (h) headerComponent?.style.setProperty('--search-bar-top-height', `${h}px`);
      };
      setTopHeight();
      const signal = this.abortController.signal;
      window.addEventListener('resize', setTopHeight, { signal });
    }

    // Mirror 'searching' class from predictive search to actions loading-state
    if (this.#predictiveSearch) {
      this.#searchingObserver = new MutationObserver(() => {
        const isSearching = this.#predictiveSearch.classList.contains('searching');
        this.refs.actions?.classList.toggle('loading-state', isSearching);
      });
      this.#searchingObserver.observe(this.#predictiveSearch, { attributes: true, attributeFilter: ['class'] });
    }

    const { searchInput } = this.refs;
    if (!searchInput) return;

    const signal = this.abortController.signal;
    searchInput.addEventListener('focus', this.#handleFocus, { signal });
    searchInput.addEventListener('input', this.#handleInput, { signal });
    searchInput.addEventListener('keydown', this.#handleKeydown, { signal });
    // Reset sits between the input and the results in tab order while open.
    this.#resetButton?.addEventListener('keydown', this.#handleActionsKeydown, { signal });
    document.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    document.addEventListener(MenuDropdownOpenEvent.eventName, this.#handleMenuOrDialogOpen, { signal });
    document.addEventListener(DropdownDialogOpenEvent.eventName, this.#handleMenuOrDialogOpen, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeCursorListeners();
    this.closest('header-component')?.classList.remove('has-search-bar-top');
    if (this.#container?.parentNode === document.body) document.body.removeChild(this.#container);
    if (this.#overlay?.parentNode === document.body) document.body.removeChild(this.#overlay);
    this.#resizeHandler?.cancel?.();
    window.removeEventListener('resize', this.#resizeHandler);
    this.#searchingObserver?.disconnect();
    this.#container = null;
    this.#overlay = null;
    this.#predictiveSearch = null;
    this.#resizeHandler = null;
    this.#searchingObserver = null;
  }

  get #resetButton() {
    return this.refs.actions?.querySelector('.search-bar__reset') ?? null;
  }

  #setupCursorListeners(overlay) {
    if (!overlay) return;

    this.#cursorBinding?.stop();
    this.#cursorBinding = bindCloseCursor({
      root: overlay,
      except: () => this.#container,
      mask: [overlay, [document.documentElement, 'search-cursor-active']],
    });
  }

  #removeCursorListeners() {
    this.#cursorBinding?.stop();
    this.#cursorBinding = null;
  }

  openPopup() {
    if (this.#isOpen) return;
    this.#isOpen = true;
    this.classList.add('search-bar--open');

    // Cancel any in-progress close animation so the panel re-opens cleanly
    this.#container?.classList.remove('search-bar-closing');
    this.#overlay?.classList.remove('search-bar-closing');

    // Sync current input value to hidden predictive-search input
    const psInput = this.#predictiveSearch?.refs.predictiveSearchInput;
    if (psInput) psInput.value = this.refs.searchInput?.value ?? '';

    this.#container?.removeAttribute('hidden');
    this.#overlay?.removeAttribute('hidden');

    // RAF ensures panel has layout dimensions before positioning
    requestAnimationFrame(() => {
      this.#position();
      this.#setupCursorListeners(this.#overlay);
    });

    if (!this.#resizeHandler) {
      this.#resizeHandler = debounce(() => this.#position(), 100);
      window.addEventListener('resize', this.#resizeHandler);
    }

    if (this.closest('.header__row--top')) {
      this.closest('header-component')?.classList.add('search-bar-top-open');
    }
    document.documentElement.setAttribute('scroll-lock', '');
    this.dispatchEvent(new DropdownDialogOpenEvent());
  }

  async closePopup() {
    if (!this.#isOpen) return;
    this.#isOpen = false;
    this.classList.remove('search-bar--open');
    this.dispatchEvent(new DropdownDialogCloseEvent());
    this.#removeCursorListeners();

    const container = this.#container;
    if (!container) return;

    container.classList.add('search-bar-closing');
    this.#overlay?.classList.add('search-bar-closing');
    await new Promise(r => requestAnimationFrame(r));
    await onAnimationEnd(this.#container, undefined, { subtree: false });
    container.classList.remove('search-bar-closing');
    this.#overlay?.classList.remove('search-bar-closing');
    if (!this.#isOpen) {
      container.setAttribute('hidden', '');
      this.#overlay?.setAttribute('hidden', '');
      this.closest('header-component')?.classList.remove('search-bar-top-open');
      if (!document.querySelector('.dialog[open]') && !document.querySelector('details[is="mega-dropdown"][open]') && !document.querySelector('details[is="menu-dropdown"][open]')) {
        document.documentElement.removeAttribute('scroll-lock');
      }
    }
  }

  clearSearch() {
    const input = this.refs.searchInput;
    if (input) input.value = '';
    this.#predictiveSearch?.resetSearch(false);
    this.closePopup();
  }

  /**
   * Opens the search panel and focuses the input (the search icon acts as an
   * open trigger, not a form submit).
   */
  openSearch() {
    this.openPopup();
    this.refs.searchInput?.focus();
  }

  #handleMenuOrDialogOpen = (e) => {
    if (e.target === this) return;
    if (this.#isOpen) {
      this.closePopup();
      this.refs.searchInput?.blur();
    }
  }

  #handleFocus = () => {
    if (!this.refs.searchInput?.value.trim() && !this.#emptyStateHasContent()) return;
    this.openPopup();
  }

  /**
   * Whether the predictive-search empty state has anything to show: it must be
   * visible (not hidden) and the empty-state element must contain content.
   * @returns {boolean}
   */
  #emptyStateHasContent() {
    const empty = this.#predictiveSearch?.refs.predictiveSearchEmpty;
    if (!empty || empty.hidden) return false;
    return !!empty.querySelector('.predictive-search-empty-state')?.children.length;
  }

  #handleInput = () => {
    const val = this.refs.searchInput?.value ?? '';
    const ps = this.#predictiveSearch;
    if (!ps?.dataset.sectionId) return;
    const psInput = ps.refs.predictiveSearchInput;
    if (psInput) psInput.value = val;
    if (val.trim()) {
      // Reopen if the user resumed typing after closing with Escape.
      if (!this.#isOpen) this.openPopup();
      ps.search({ inputType: 'insertText' });
    } else {
      ps.resetSearch(false);
    }
  }

  #handleKeydown = (e) => {
    if (e.key === 'Escape') {
      // Close the panel but keep focus on the input (combobox behaviour) so the
      // user can keep typing to reopen results.
      this.closePopup();
      return;
    }
    if (e.key === 'Enter') {
      this.#predictiveSearch?.querySelector('form')?.requestSubmit();
      return;
    }
    if (e.key === 'Tab' && e.shiftKey && this.#isOpen) {
      // Shift+Tab leaves backwards → dismiss the panel, let focus move naturally.
      this.closePopup();
    }
    // Tab forward falls through to native focus → the reset button (search-bar__reset,
    // shown in place of submit while open), which then routes into the results panel.
  }

  /**
   * Visible, focusable items inside the results panel (excludes the input/actions
   * form and anything in a hidden container).
   * @returns {HTMLElement[]}
   */
  #getResultItems() {
    const content = this.#predictiveSearch?.querySelector('[ref="predictiveSearchContent"]');
    if (!content) return [];
    return getFocusableElements(content).filter((el) => el.offsetParent !== null);
  }

  /**
   * Tab/Shift+Tab moves focus linearly through result items. Shift+Tab at the first
   * item returns to the reset button; Tab past the last item dismisses the panel and
   * moves on to the next element after the search bar. Escape returns to the input.
   * @param {KeyboardEvent} e
   */
  #handleResultsKeydown = (e) => {
    if (e.key === 'Escape') {
      this.refs.searchInput?.focus();
      this.closePopup();
      return;
    }
    if (e.key !== 'Tab') return;

    const items = this.#getResultItems();
    const current = items.indexOf(document.activeElement);
    if (current === -1) return;

    e.preventDefault();
    if (e.shiftKey) {
      // Backwards: previous item, or back to the reset button from the first item.
      if (current === 0) (this.#resetButton ?? this.refs.searchInput)?.focus();
      else items[current - 1]?.focus();
    } else if (current === items.length - 1) {
      // Forwards past the last item: leave the search entirely.
      this.closePopup();
      this.#focusNextAfterSearch();
    } else {
      items[current + 1]?.focus();
    }
  }

  /**
   * The reset button sits between the input and the results. Tab forward routes into
   * the first result (or out if there are none); Shift+Tab falls through to the input.
   * @param {KeyboardEvent} e
   */
  #handleActionsKeydown = (e) => {
    if (!this.#isOpen) return;
    if (e.key === 'Escape') {
      this.refs.searchInput?.focus();
      this.closePopup();
      return;
    }
    if (e.key !== 'Tab' || e.shiftKey) return;

    e.preventDefault();
    const [first] = this.#getResultItems();
    if (first) {
      first.focus();
    } else {
      this.closePopup();
      this.#focusNextAfterSearch();
    }
  }

  /**
   * Focuses the first focusable element after the search bar in document order,
   * skipping anything inside the search bar or the body-appended results panel.
   */
  #focusNextAfterSearch() {
    const next = getFocusableElements(document.body).find(
      (el) =>
        !this.contains(el) &&
        !this.#container?.contains(el) &&
        Boolean(this.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)
    );
    next?.focus();
  }

  #handlePointerDown = (e) => {
    if (this.contains(e.target) || this.#container?.contains(e.target)) return;
    // A dialog opened from the results — quick add, quick view — stacks above the popup,
    // so a press inside it is still a press on the popup's own flow, not outside it.
    if (e.target instanceof Element && e.target.closest('[ref="dialog"][open]')) return;
    this.closePopup();
  }

  #position() {
    if (!this.#container) return;
    const vPad = SearchBarComponent.VIEWPORT_PADDING;
    const triggerRect = this.getBoundingClientRect();
    const panelWidth = this.#container.offsetWidth;

    const containerRect = (this.closest('.header__container') || this).getBoundingClientRect();
    const top = Math.round(containerRect.bottom);
    this.#container.style.top = `${top}px`;

    if (this.#overlay) {
      this.#overlay.style.top = `${top}px`;

      if(this.closest('.header-sticky--boxed') && this.closest('.header-section.header-scrolling')) {
        this.#overlay.style.top = `0px`;
      }
    }

    // Panel left-aligns with trigger; if it overflows right, shift so right edge meets trigger right edge
    let left = triggerRect.left;
    if (left + panelWidth > window.innerWidth - vPad) {
      left = triggerRect.right - panelWidth;
    }
    // Clamp within viewport
    left = Math.max(vPad, Math.min(left, window.innerWidth - vPad - panelWidth));
    this.#container.style.left = `${Math.round(left)}px`;
  }
}

if (!customElements.get('search-bar-component')) {
  customElements.define('search-bar-component', SearchBarComponent);
}